import {
  DOMAIN_REGEX,
  POLL_OPTIONS_MIN,
  POLL_QUESTION_MIN_LENGTH,
  SPEAKER_NAME_REGEX,
  SURVEY_QUESTION_MIN_LENGTH,
  WEBINAR_DURATION_MAX_MINUTES,
  WEBINAR_DURATION_MIN_MINUTES,
  WEBINAR_EMAIL_REGEX,
  WEBINAR_ERROR_CODES,
  WEBINAR_ERROR_MESSAGES,
  WEBINAR_HOST_NAME_REGEX,
  WEBINAR_MAX_REGISTRANTS_MAX,
  WEBINAR_MAX_REGISTRANTS_MIN,
  WEBINAR_PASSWORD_MIN_LENGTH,
  WEBINAR_TITLE_MAX_LENGTH,
  WEBINAR_TITLE_MIN_LENGTH,
  WEBINAR_TITLE_REGEX,
} from '../constants/webinar.constants'
import type { WebinarValidationErrors } from '../services/webinar-error'
import type { WebinarFormData } from '../steps/types'

const M = WEBINAR_ERROR_MESSAGES

/** Step 0 – core details (title, date/time, duration, host, password). */
export function validateStepBasics(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  const title = data.title.trim()
  if (!title) errors.title = M[WEBINAR_ERROR_CODES.TITLE_REQUIRED]
  else if (title.length < WEBINAR_TITLE_MIN_LENGTH)
    errors.title = M[WEBINAR_ERROR_CODES.TITLE_TOO_SHORT]
  else if (title.length > WEBINAR_TITLE_MAX_LENGTH)
    errors.title = `Title cannot exceed ${WEBINAR_TITLE_MAX_LENGTH} characters.`
  else if (!WEBINAR_TITLE_REGEX.test(title))
    errors.title = M[WEBINAR_ERROR_CODES.TITLE_INVALID_CHARS]

  if (!data.date) {
    errors.date = M[WEBINAR_ERROR_CODES.DATE_REQUIRED]
  } else {
    const sel = new Date(data.date)
    sel.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (sel.getTime() < today.getTime())
      errors.date = M[WEBINAR_ERROR_CODES.DATE_PAST]
  }

  if (!data.time) errors.time = M[WEBINAR_ERROR_CODES.TIME_REQUIRED]

  if (data.date && data.time) {
    const start = new Date(data.date)
    const [hh, mm] = data.time.split(':').map(Number)
    start.setHours(hh, mm, 0, 0)
    if (start.getTime() < Date.now())
      errors.time = M[WEBINAR_ERROR_CODES.START_IN_PAST]
  }

  const durationTotal = data.durationHrs * 60 + data.durationMins
  if (!Number.isFinite(durationTotal) || durationTotal < WEBINAR_DURATION_MIN_MINUTES)
    errors.duration = M[WEBINAR_ERROR_CODES.DURATION_INVALID]
  else if (durationTotal > WEBINAR_DURATION_MAX_MINUTES)
    errors.duration = M[WEBINAR_ERROR_CODES.DURATION_TOO_LONG]

  const hostName = data.hostName.trim()
  if (!hostName) errors.hostName = M[WEBINAR_ERROR_CODES.HOST_NAME_REQUIRED]
  else if (!WEBINAR_HOST_NAME_REGEX.test(hostName))
    errors.hostName = M[WEBINAR_ERROR_CODES.HOST_NAME_INVALID]

  const hostEmail = data.hostEmail.trim()
  if (!hostEmail) errors.hostEmail = M[WEBINAR_ERROR_CODES.HOST_EMAIL_REQUIRED]
  else if (!WEBINAR_EMAIL_REGEX.test(hostEmail))
    errors.hostEmail = M[WEBINAR_ERROR_CODES.HOST_EMAIL_INVALID]

  if (data.passwordEnabled) {
    if (!data.password || data.password.length < WEBINAR_PASSWORD_MIN_LENGTH)
      errors.password = M[WEBINAR_ERROR_CODES.PASSWORD_TOO_SHORT]
  }

  if (
    !Number.isFinite(data.maxRegistrants) ||
    data.maxRegistrants < WEBINAR_MAX_REGISTRANTS_MIN ||
    data.maxRegistrants > WEBINAR_MAX_REGISTRANTS_MAX
  ) {
    errors.maxRegistrants = M[WEBINAR_ERROR_CODES.MAX_REGISTRANTS_INVALID]
  }
}

/** Step 1 – registration. Only validates when registration is enabled. */
export function validateStepRegistration(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  if (!data.withRegistration) return

  if (!data.registrationFields || data.registrationFields.length === 0) {
    errors.registrationFields = M[WEBINAR_ERROR_CODES.REG_FIELDS_REQUIRED]
  }

  if (data.domainRestrictionEnabled) {
    const list =
      data.domainRestrictionType === 1
        ? data.authorizedDomains
        : data.restrictedDomains
    if (!list || list.length === 0) {
      errors.domainList = M[WEBINAR_ERROR_CODES.DOMAIN_LIST_EMPTY]
    } else {
      const bad = list.find((d) => !DOMAIN_REGEX.test(d))
      if (bad) errors.domainList = `${M[WEBINAR_ERROR_CODES.DOMAIN_INVALID]} (${bad})`
    }
  }

  if (
    !Number.isFinite(data.registrationLimit) ||
    data.registrationLimit < WEBINAR_MAX_REGISTRANTS_MIN
  ) {
    errors.registrationLimit = M[WEBINAR_ERROR_CODES.MAX_REGISTRANTS_INVALID]
  }
}

/** Step 2 – notifications. Each enabled reminder/email must have time > 0. */
export function validateStepNotifications(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  const { reminders, postWebinar } = data
  const checkReminder = (key: string, enabled: boolean, time: number) => {
    if (enabled && (!Number.isFinite(time) || time <= 0)) {
      errors[key] = M[WEBINAR_ERROR_CODES.REMINDER_TIME_INVALID]
    }
  }
  checkReminder(
    'firstReminder',
    reminders.firstReminder.enabled,
    reminders.firstReminder.time,
  )
  checkReminder(
    'secondReminder',
    reminders.secondReminder.enabled,
    reminders.secondReminder.time,
  )
  checkReminder(
    'thirdReminder',
    reminders.thirdReminder.enabled,
    reminders.thirdReminder.time,
  )

  const checkPostEmail = (key: string, enabled: boolean, time: number) => {
    if (enabled && (!Number.isFinite(time) || time <= 0)) {
      errors[key] = M[WEBINAR_ERROR_CODES.POST_EMAIL_TIME_INVALID]
    }
  }
  checkPostEmail(
    'thankYouEmail',
    postWebinar.thankYouEmail.enabled,
    postWebinar.thankYouEmail.time,
  )
  checkPostEmail(
    'followUpEmail',
    postWebinar.followUpEmail.enabled,
    postWebinar.followUpEmail.time,
  )
}

const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
const HTTP_URL_REGEX = /^https?:\/\/.+\..+/i
const IMAGE_URL_REGEX = /\.(png|jpe?g|svg|webp|gif)(\?.*)?$/i
const VIDEO_URL_REGEX = /\.(mp4|webm|mov|ogg)(\?.*)?$/i

/** Step 3 – branding. Validates hex colors and any provided media URLs. */
export function validateStepBranding(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  const primary = data.webinarTheme?.primary?.trim() ?? ''
  if (!primary) errors.themePrimary = 'Pick a primary colour.'
  else if (!HEX_COLOR_REGEX.test(primary))
    errors.themePrimary = 'Primary colour must be a valid hex code (e.g. #4F46E5).'

  const navBar = data.webinarTheme?.navBar?.trim() ?? ''
  if (!navBar) errors.themeNavBar = 'Pick a nav bar colour.'
  else if (!HEX_COLOR_REGEX.test(navBar))
    errors.themeNavBar = 'Nav bar colour must be a valid hex code (e.g. #1E293B).'

  const logo = (data as { logoUrl?: string }).logoUrl?.trim()
  if (logo) {
    if (!HTTP_URL_REGEX.test(logo))
      errors.logoUrl = 'Logo URL must start with http:// or https://.'
    else if (!IMAGE_URL_REGEX.test(logo))
      errors.logoUrl = 'Logo must be a PNG, JPG, SVG, WEBP, or GIF.'
  }

  const bg = (data as { backgroundUrl?: string }).backgroundUrl?.trim()
  if (bg) {
    if (!HTTP_URL_REGEX.test(bg))
      errors.backgroundUrl = 'Background URL must start with http:// or https://.'
    else if (!IMAGE_URL_REGEX.test(bg))
      errors.backgroundUrl = 'Background must be a PNG, JPG, SVG, WEBP, or GIF.'
  }

  const intro = (data as { introVideoUrl?: string }).introVideoUrl?.trim()
  if (intro) {
    if (!HTTP_URL_REGEX.test(intro))
      errors.introVideoUrl = 'Intro video URL must start with http:// or https://.'
    else if (!VIDEO_URL_REGEX.test(intro))
      errors.introVideoUrl = 'Intro video must be MP4, WebM, MOV, or OGG.'
  }
}

/** Step 4 – speakers. Each speaker that's been added must be valid. */
export function validateStepSpeakers(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  data.speakers.forEach((s, i) => {
    const key = (suffix: string) => `speakers[${i}].${suffix}`
    const name = s.speakerName?.trim() ?? ''
    if (!name) errors[key('name')] = M[WEBINAR_ERROR_CODES.SPEAKER_NAME_REQUIRED]
    else if (!SPEAKER_NAME_REGEX.test(name))
      errors[key('name')] = M[WEBINAR_ERROR_CODES.SPEAKER_NAME_INVALID]
    const email = s.speakerEmail?.trim() ?? ''
    if (email && !WEBINAR_EMAIL_REGEX.test(email))
      errors[key('email')] = M[WEBINAR_ERROR_CODES.SPEAKER_EMAIL_INVALID]
  })
}

/** Step 5 – polls. Each poll that's been added must be valid. */
export function validateStepPolls(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  data.polls.forEach((poll, i) => {
    const key = (suffix: string) => `polls[${i}].${suffix}`
    const q = poll.question?.trim() ?? ''
    if (!q || q.length < POLL_QUESTION_MIN_LENGTH)
      errors[key('question')] = M[WEBINAR_ERROR_CODES.POLL_QUESTION_REQUIRED]
    const validOptions = poll.options.filter((o) => o.text?.trim()).length
    if (validOptions < POLL_OPTIONS_MIN)
      errors[key('options')] = M[WEBINAR_ERROR_CODES.POLL_NEEDS_TWO_OPTIONS]
  })
}

/** Step 6 – survey. Optional — but if a survey object exists it must have a
 * title and at least one question. */
export function validateStepSurveys(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  if (!data.survey) return
  if (!data.survey.title?.trim())
    errors.surveyTitle = M[WEBINAR_ERROR_CODES.SURVEY_TITLE_REQUIRED]
  if (!data.survey.questions || data.survey.questions.length === 0)
    errors.surveyQuestions = M[WEBINAR_ERROR_CODES.SURVEY_QUESTION_REQUIRED]
  data.survey.questions?.forEach((q, i) => {
    const key = (suffix: string) => `survey.questions[${i}].${suffix}`
    if (!q.question?.trim() || q.question.trim().length < SURVEY_QUESTION_MIN_LENGTH)
      errors[key('text')] = M[WEBINAR_ERROR_CODES.SURVEY_QUESTION_REQUIRED]
  })
}

/** Step 7 – engagement. Cross-step consistency: enabled features must have
 *  matching content in earlier steps. */
export function validateStepEngagement(
  data: WebinarFormData,
  errors: WebinarValidationErrors,
): void {
  if (data.pollsEnabled && (!data.polls || data.polls.length === 0)) {
    errors.pollsEnabled =
      'Polls are enabled but no polls have been added. Add at least one poll in Step 5 or disable polls.'
  }
  if (
    data.surveysEnabled &&
    (!data.survey || !data.survey.questions || data.survey.questions.length === 0)
  ) {
    errors.surveysEnabled =
      'Surveys are enabled but no survey has been created. Add a survey in Step 6 or disable surveys.'
  }
}

export type StepIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

export const STEP_VALIDATORS: Record<
  StepIndex,
  (data: WebinarFormData, errors: WebinarValidationErrors) => void
> = {
  0: validateStepBasics,
  1: validateStepRegistration,
  2: validateStepNotifications,
  3: validateStepBranding,
  4: validateStepSpeakers,
  5: validateStepPolls,
  6: validateStepSurveys,
  7: validateStepEngagement,
}

export function validateWebinarStep(
  step: StepIndex,
  data: WebinarFormData,
): WebinarValidationErrors {
  const errors: WebinarValidationErrors = {}
  STEP_VALIDATORS[step](data, errors)
  return errors
}

export function generateWebinarId(): string {
  return String(Math.floor(1_000_000_000 + Math.random() * 9_000_000_000))
}
